import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/axios";
import Layout from "../components/Layout";
import Card from "../components/UI/Card";
import Button from "../components/UI/Button";
import { PageLoader } from "../components/UI/Spinner";
import { useAuth } from "../context/AuthContext";

function can(permissions, path, userRole) {
  if (userRole === "SUPER_ADMIN") return true;
  return path.split(".").reduce((a, k) => (a ? a[k] : undefined), permissions) === true;
}

// Parses any date format → { dd, mm, yyyy } or null
function parseAnyDate(v) {
  if (!v) return null;
  const s = String(v).trim();

  // YYYY-MM-DD (ISO / from date picker)
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return { dd: iso[3], mm: iso[2], yyyy: iso[1] };

  // "Aug 30 2023 11:59PM" — month-name style
  const MONTHS = { Jan:"01",Feb:"02",Mar:"03",Apr:"04",May:"05",Jun:"06",Jul:"07",Aug:"08",Sep:"09",Oct:"10",Nov:"11",Dec:"12" };
  const mname = /^([A-Za-z]{3})\s+(\d{1,2})\s+(\d{4})/.exec(s);
  if (mname && MONTHS[mname[1]]) return { dd: mname[2].padStart(2,"0"), mm: MONTHS[mname[1]], yyyy: mname[3] };

  // D/M/YYYY or DD/MM/YYYY or M/D/YYYY
  const parts = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (parts) {
    const [, a, b, yyyy] = parts;
    const na = Number(a), nb = Number(b);
    if (na > 12) return { dd: a.padStart(2,"0"), mm: b.padStart(2,"0"), yyyy };
    if (nb > 12) return { dd: b.padStart(2,"0"), mm: a.padStart(2,"0"), yyyy };
    return { dd: a.padStart(2,"0"), mm: b.padStart(2,"0"), yyyy }; // assume DD/MM
  }

  return null;
}

// For HTML5 date input value (needs YYYY-MM-DD)
function toDateValue(v) {
  const d = parseAnyDate(v);
  return d ? `${d.yyyy}-${d.mm}-${d.dd}` : "";
}

// For display (DD/MM/YYYY)
function toDisplayDate(v) {
  const d = parseAnyDate(v);
  return d ? `${d.dd}/${d.mm}/${d.yyyy}` : "";
}

// Convert YYYY-MM-DD from date picker → DD/MM/YYYY for storage
function pickerToddmmyyyy(isoVal) {
  if (!isoVal) return "";
  const [y, m, d] = isoVal.split("-");
  return `${d}/${m}/${y}`;
}

const INDIAN_STATES = [
  "Andaman and Nicobar Islands","Andhra Pradesh","Arunachal Pradesh","Assam","Bihar",
  "Chandigarh","Chhattisgarh","Dadra and Nagar Haveli and Daman and Diu","Delhi","Goa",
  "Gujarat","Haryana","Himachal Pradesh","Jammu and Kashmir","Jharkhand","Karnataka",
  "Kerala","Ladakh","Lakshadweep","Madhya Pradesh","Maharashtra","Manipur","Meghalaya",
  "Mizoram","Nagaland","Odisha","Puducherry","Punjab","Rajasthan","Sikkim","Tamil Nadu",
  "Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal"
];

const STATE_CITIES = {
  "Andaman and Nicobar Islands": ["Port Blair","Diglipur","Rangat","Mayabunder","Car Nicobar"],
  "Andhra Pradesh": ["Visakhapatnam","Vijayawada","Guntur","Nellore","Kurnool","Tirupati","Rajahmundry","Kadapa","Kakinada","Anantapur","Eluru","Ongole","Nandyal","Machilipatnam","Adoni","Tenali","Proddatur","Chittoor","Hindupur","Bhimavaram","Madanapalle","Guntakal","Dharmavaram","Gudivada","Narasaraopet","Tadipatri","Mangalagiri","Chilakaluripet"],
  "Arunachal Pradesh": ["Itanagar","Naharlagun","Pasighat","Tezpur","Bomdila","Ziro","Along","Tezu","Khonsa","Aalo"],
  "Assam": ["Guwahati","Silchar","Dibrugarh","Jorhat","Nagaon","Tinsukia","Tezpur","Bongaigaon","Dhubri","Diphu","North Lakhimpur","Sivasagar","Goalpara","Barpeta","Karimganj","Hailakandi","Nalbari","Golaghat","Morigaon","Mangaldoi"],
  "Bihar": ["Patna","Gaya","Bhagalpur","Muzaffarpur","Purnia","Darbhanga","Arrah","Begusarai","Katihar","Munger","Chhapra","Hajipur","Samastipur","Siwan","Motihari","Nawada","Saharsa","Sitamarhi","Dehri","Bettiah","Aurangabad","Buxar","Jehanabad","Khagaria","Kishanganj"],
  "Chandigarh": ["Chandigarh","Manimajra","Burail","Hallomajra","Daria","Mauli Jagran"],
  "Chhattisgarh": ["Raipur","Bhilai","Bilaspur","Korba","Durg","Rajnandgaon","Jagdalpur","Ambikapur","Raigarh","Dhamtari","Chirmiri","Bhatapara","Dongargarh","Dalli Rajhara","Naila Janjgir","Tilda","Mungeli","Chamba","Kawardha","Mahasamund"],
  "Dadra and Nagar Haveli and Daman and Diu": ["Silvassa","Daman","Diu","Amli","Khanvel","Naroli"],
  "Delhi": ["New Delhi","Dwarka","Rohini","Janakpuri","Laxmi Nagar","Saket","Pitampura","Preet Vihar","Shahdara","Mayur Vihar","Uttam Nagar","Vikaspuri","Paschim Vihar","Vasant Kunj","Malviya Nagar","Hauz Khas","Karol Bagh","Connaught Place","Chandni Chowk","Narela"],
  "Goa": ["Panaji","Margao","Vasco da Gama","Mapusa","Ponda","Bicholim","Curchorem","Sanquelim","Canacona","Quepem"],
  "Gujarat": ["Ahmedabad","Surat","Vadodara","Rajkot","Bhavnagar","Jamnagar","Junagadh","Gandhinagar","Anand","Navsari","Morbi","Nadiad","Surendranagar","Bharuch","Mehsana","Bhuj","Porbandar","Palanpur","Valsad","Amreli","Botad","Dahod","Godhra","Kapadvanj","Veraval","Gandhidham","Gondal","Jetpur","Wankaner","Ankleshwar"],
  "Haryana": ["Faridabad","Gurgaon","Panipat","Ambala","Yamunanagar","Rohtak","Hisar","Karnal","Sonipat","Panchkula","Bhiwani","Sirsa","Bahadurgarh","Rewari","Jhajjar","Jind","Fatehabad","Mahendragarh","Palwal","Pehowa","Kurukshetra","Kaithal","Hansi","Narnaul"],
  "Himachal Pradesh": ["Shimla","Solan","Dharamshala","Kullu","Mandi","Palampur","Baddi","Nahan","Hamirpur","Chamba","Una","Bilaspur","Rampur","Sundernagar","Kangra"],
  "Jammu and Kashmir": ["Srinagar","Jammu","Anantnag","Baramulla","Sopore","Udhampur","Kathua","Poonch","Rajouri","Pulwama","Kupwara","Kulgam","Bandipora","Ganderbal"],
  "Jharkhand": ["Ranchi","Jamshedpur","Dhanbad","Bokaro","Deoghar","Phusro","Hazaribagh","Giridih","Ramgarh","Medininagar","Chirkunda","Sahibganj","Adityapur","Chaibasa","Dumka"],
  "Karnataka": ["Bengaluru","Mysuru","Mangaluru","Belagavi","Kalaburagi","Ballari","Vijayapura","Shivamogga","Tumkur","Raichur","Bidar","Dharwad","Gadag","Hassan","Udupi","Davangere","Chitradurga","Kolar","Mandya","Chikkamagaluru","Bagalkot","Haveri","Yadgir","Chamarajanagar","Kodagu","Ramanagara"],
  "Kerala": ["Thiruvananthapuram","Kochi","Kozhikode","Thrissur","Kollam","Alappuzha","Palakkad","Malappuram","Kottayam","Kannur","Kasaragod","Pathanamthitta","Idukki","Wayanad","Ernakulam","Munnar"],
  "Ladakh": ["Leh","Kargil","Diskit","Zanskar","Nubra"],
  "Lakshadweep": ["Kavaratti","Agatti","Amini","Andrott","Minicoy"],
  "Madhya Pradesh": ["Bhopal","Indore","Jabalpur","Gwalior","Ujjain","Sagar","Dewas","Satna","Ratlam","Rewa","Murwara","Singrauli","Burhanpur","Khandwa","Bhind","Chhindwara","Guna","Shivpuri","Vidisha","Chhatarpur","Damoh","Mandsaur","Khargone","Neemuch","Pithampur","Hoshangabad","Itarsi","Sehore","Seoni","Mandla","Datia","Mhow"],
  "Maharashtra": ["Mumbai","Pune","Nagpur","Thane","Nashik","Aurangabad","Solapur","Amravati","Navi Mumbai","Kolhapur","Sangli","Malegaon","Jalgaon","Akola","Latur","Dhule","Ahmednagar","Chandrapur","Parbhani","Ichalkaranji","Jalna","Ambernath","Bhiwandi","Nanded","Satara","Vasai","Beed","Ratnagiri","Osmanabad","Gondia","Wardha","Yavatmal","Buldhana"],
  "Manipur": ["Imphal","Thoubal","Bishnupur","Senapati","Churachandpur","Ukhrul","Jiribam","Moreh","Kakching"],
  "Meghalaya": ["Shillong","Tura","Jowai","Nongstoin","Williamnagar","Baghmara","Resubelpara"],
  "Mizoram": ["Aizawl","Lunglei","Champhai","Kolasib","Serchhip","Mamit","Lawngtlai","Siaha"],
  "Nagaland": ["Kohima","Dimapur","Mokokchung","Tuensang","Wokha","Zunheboto","Mon","Phek","Longleng","Kiphire","Peren"],
  "Odisha": ["Bhubaneswar","Cuttack","Rourkela","Brahmapur","Sambalpur","Puri","Balasore","Bhadrak","Baripada","Jharsuguda","Bargarh","Paradip","Bhawanipatna","Dhenkanal","Balangir","Sundergarh","Phulbani","Rayagada","Jagatsinghpur","Kendujhar"],
  "Puducherry": ["Puducherry","Karaikal","Mahe","Yanam","Ozhukarai","Villianur"],
  "Punjab": ["Ludhiana","Amritsar","Jalandhar","Patiala","Bathinda","Mohali","Hoshiarpur","Batala","Pathankot","Moga","Abohar","Malerkotla","Khanna","Phagwara","Muktsar","Barnala","Rajpura","Firozpur","Kapurthala","Sangrur"],
  "Rajasthan": ["Jaipur","Jodhpur","Kota","Bikaner","Ajmer","Udaipur","Bhilwara","Alwar","Bharatpur","Sikar","Pali","Sri Ganganagar","Kishangarh","Baran","Dhaulpur","Tonk","Beawar","Hanumangarh","Gangapur City","Nagaur","Jhunjhunu","Sawai Madhopur","Chittorgarh","Barmer","Jhalawar"],
  "Sikkim": ["Gangtok","Namchi","Mangan","Gyalshing","Rangpo","Jorethang"],
  "Tamil Nadu": ["Chennai","Coimbatore","Madurai","Tiruchirappalli","Salem","Tirunelveli","Tiruppur","Vellore","Erode","Thoothukudi","Dindigul","Thanjavur","Ranipet","Sivakasi","Karur","Udhagamandalam","Hosur","Nagercoil","Kanchipuram","Kumbakonam","Cuddalore","Kanyakumari","Rajapalayam","Ambattur","Avadi"],
  "Telangana": ["Hyderabad","Warangal","Nizamabad","Khammam","Karimnagar","Ramagundam","Mahabubnagar","Nalgonda","Adilabad","Suryapet","Miryalaguda","Siddipet","Jagtial","Mancherial","Nirmal","Vikarabad","Bhadrachalam","Kothagudem","Narayanpet","Wanaparthy"],
  "Tripura": ["Agartala","Udaipur","Dharmanagar","Kailashahar","Belonia","Khowai","Sabroom","Ambassa","Kumarghat","Teliamura"],
  "Uttar Pradesh": ["Lucknow","Kanpur","Ghaziabad","Agra","Meerut","Varanasi","Prayagraj","Bareilly","Aligarh","Moradabad","Saharanpur","Gorakhpur","Noida","Firozabad","Loni","Jhansi","Muzaffarnagar","Mathura","Rampur","Shahjahanpur","Farrukhabad","Mau","Hapur","Etawah","Mirzapur","Bulandshahr","Sambhal","Amroha","Hardoi","Fatehpur","Raebareli","Orai","Sitapur","Bahraich","Modinagar","Unnao","Jaunpur","Lakhimpur","Hathras","Banda","Pilibhit","Barabanki","Khurja","Gonda","Mainpuri","Lalitpur","Etah","Deoria","Bijnor","Sultanpur"],
  "Uttarakhand": ["Dehradun","Haridwar","Roorkee","Haldwani","Rudrapur","Kashipur","Rishikesh","Kotdwar","Pithoragarh","Ramnagar","Mussoorie","Nainital","Almora","Lansdowne","Pauri","Tehri","Uttarkashi","Chamoli","Bageshwar","Champawat"],
  "West Bengal": ["Kolkata","Asansol","Siliguri","Durgapur","Bardhaman","Maheshtala","Rajpur Sonarpur","South Dumdum","Howrah","Bidhannagar","North Dumdum","Barasat","Barddhaman","Kharagpur","Haldia","Jalpaiguri","Darjeeling","Cooch Behar","Bankura","Krishnanagar","Santipur","Nabadwip","Medinipur","Ranaghat","Balurghat","Malda","Raiganj","Habra","Basirhat","Bongaon"],
};

const FIELD_CONFIG = {
  POLICY_START_DATE:           { type: "date" },
  OD_POLICY_EXPIRY_DATE:       { type: "date" },
  TPPOLICY_EXPIRY_DATE:        { type: "date" },
  OTHER_TP_POLICY_EXPIRY_DATE: { type: "date" },
  INVOICE_DATE:                { type: "date" },
  DOB:                         { type: "date" },
  CUSTOMER_MOBILE_NO:          { type: "phone" },
  "ALTERNATE_NO 1":            { type: "phone" },
  "ALTERNATE_NO 2":            { type: "phone" },
  REMARK_DATE:                 { type: "date" },
  follow_up_date:                 { type: "date" },
  dhwani_updated_at:              { type: "date" },
  dhwani_call_started:            { type: "date" },
  dhwani_completed_at:            { type: "date" },

  DEALER_STATE:  { type: "select", options: INDIAN_STATES },
  ClaimStatus:  { type: "select", options: ["YES", "NO"] },
  DEALER_CITY:   { type: "select", cascade: "DEALER_STATE", options: STATE_CITIES },
  Policy_Type:   { type: "select", options: ["N", "R"] },
  CoverType:   { type: "select", options: ["1 OD + 0 TP", "1 OD + 1 TP" , "1 OD + 3 TP"] },
  VehicleClass:   { type: "select", options: ["Private", "Commercial"] },
  MODEL_NAME:    { type: "select", addable: true, options: ["ALCAZAR","ALL NEW I20","ALL NEW TUCSON","ALL NEW VERNA","AURA","CRETA","EON","EXTER","GRAND I10","NEW I20 (IB)","SANTRO","VENUE","VERNA","VERNA FLUIDIC","XCENT"] },
  STATUS:        { type: "select", options: ["NOT PICKING CALL","INTRESTED","NOT INTRESTED","NOT CONTACTED"] },
};

const FIELD_LABELS = {
  DEALER_CODE: "Dealer Code", TC_NAME: "TC Name", STATUS: "Status",
  DEALER_STATE: "Dealer State", DEALER_CITY: "Dealer City",
  Zone: "Zone", Region: "Region", ClaimStatus: "Claim Status", RM_Name: "RM Name",
  Policy_Type: "Policy Type", POLICY_NO: "Policy No",
  POLICY_START_DATE: "Policy Start Date", OD_POLICY_EXPIRY_DATE: "OD Policy Expiry",
  TPPOLICY_EXPIRY_DATE: "TP Policy Expiry", OTHER_TP_POLICY_EXPIRY_DATE: "Other TP Expiry",
  OTHERTP_POLICY_NO: "Other TP Policy No", INVOICE_DATE: "Invoice Date",
  Registration_No: "Registration No", CoverType: "Cover Type", MODEL_NAME: "Model Name",
  VehicleClass: "Vehicle Class", MFG_YEAR: "Mfg Year", Tp_Tenure: "TP Tenure",
  CHASSIS_NO: "Chassis No", CUSTOMER_NAME: "Customer Name", DOB: "Date of Birth",
  CUSTOMER_MOBILE_NO: "Customer Mobile", CUSTOMER_EMAIL_ID: "Customer Email",
  INSURANCE_COMPANY: "Insurance Company",
  "ALTERNATE_NO 1": "Alternate Number 1", "ALTERNATE_NO 2": "Alternate Number 2", REMARK: "Remark" , REMARK_DATE: "Remark Date", follow_up_date: "Follow-up Date",
  // Dhwani birthday call fields
  dhwani_batch_id:      "Batch ID",
  dhwani_call_id:       "Call ID",
  dhwani_call_status:   "Call Status",
  dhwani_duration:      "Duration",
  dhwani_call_started:  "Call Started",
  dhwani_completed_at:  "Completed At",
  dhwani_hangup_cause:  "Hangup Cause",
  dhwani_recording_url: "Recording",
  dhwani_ai_summary:    "AI Summary",
  dhwani_ai_score:      "AI Score",
  dhwani_ai_issues:     "AI Issues",
  dhwani_updated_at:    "Last Updated",
};

// Field groups for better form layout
const FIELD_GROUPS = [
  {
    label: "Customer Information",
    icon: "👤",
    fields: ["CUSTOMER_NAME", "DOB", "CUSTOMER_MOBILE_NO", "CUSTOMER_EMAIL_ID", "ALTERNATE_NO 1", "ALTERNATE_NO 2" , "STATUS"],
  },
  //  {
  //   label: "Status & Assignment",
  //   icon: "📌",
  //   fields: ["TC_NAME", "STATUS", "ClaimStatus", "RM_Name"],
  // },
   {
    label: "Vehicle Information",
    icon: "🚗",
    fields: ["Registration_No", "CoverType", "MODEL_NAME", "VehicleClass", "MFG_YEAR", "Tp_Tenure", "CHASSIS_NO", "INVOICE_DATE"],
  },
  
  {
    label: "Policy Details",
    icon: "📄",
    fields: ["INSURANCE_COMPANY","Policy_Type", "POLICY_NO", "POLICY_START_DATE", "OD_POLICY_EXPIRY_DATE", "TPPOLICY_EXPIRY_DATE", "OTHER_TP_POLICY_EXPIRY_DATE", "OTHERTP_POLICY_NO"],
     hasRemarks: true,
  },
  {
    label: "Dealer Information",
    icon: "🏢",
    fields: ["DEALER_CODE", "DEALER_STATE", "DEALER_CITY", "Zone", "Region"],
  },
 
  {
    label: "Other Details",
    icon: "📝",
    fields: [ "follow_up_date" ,"TC_NAME",  "ClaimStatus", "RM_Name"],
   
  },
  {
    label: "Loyalty Program",
    icon: "📞",
    fields: [
      "dhwani_call_status", "dhwani_duration", "dhwani_call_started",
      "dhwani_completed_at", "dhwani_hangup_cause", 
      "dhwani_ai_score", "dhwani_ai_summary", "dhwani_recording_url",
      "dhwani_batch_id", "dhwani_call_id", "dhwani_updated_at"
    ],
  },
];

const FIELD_ORDER = [
  "DEALER_CODE","TC_NAME","STATUS","DEALER_STATE","DEALER_CITY","Zone","Region",
  "ClaimStatus","RM_Name","Policy_Type","POLICY_NO","POLICY_START_DATE",
  "OD_POLICY_EXPIRY_DATE","TPPOLICY_EXPIRY_DATE","OTHER_TP_POLICY_EXPIRY_DATE",
  "OTHERTP_POLICY_NO","INVOICE_DATE","Registration_No","CoverType","MODEL_NAME",
  "VehicleClass","MFG_YEAR","Tp_Tenure","CHASSIS_NO","CUSTOMER_NAME","DOB",
  "CUSTOMER_MOBILE_NO","CUSTOMER_EMAIL_ID","INSURANCE_COMPANY",
  "ALTERNATE_NO 1","ALTERNATE_NO 2","REMARK" ,"REMARK_DATE","follow_up_date"
];

const REQUIRED_FIELDS = ["CUSTOMER_NAME", "CUSTOMER_MOBILE_NO", "POLICY_NO", "DEALER_CODE","VehicleClass"];

const inputCls = "w-full mt-1 px-3 py-2 rounded-xl border text-[rgb(var(--fg))] bg-[rgb(var(--card))] border-[rgb(var(--border))] outline-none focus:border-[rgb(var(--primary))] focus:ring-2 focus:ring-[rgba(var(--primary),0.12)] transition-colors text-sm";

function AddableSelect({ value, onChange, baseOptions }) {
  // If API returns a value not in the predefined list, seed it so the select shows it
  const [extra, setExtra] = useState(() =>
    value && !baseOptions.includes(value) ? [value] : []
  );
  const [showInput, setShowInput] = useState(false);
  const [inputVal, setInputVal] = useState("");

  const all = [...new Set([...baseOptions, ...extra])];

  function handleAdd() {
    const v = inputVal.trim().toUpperCase();
    if (!v) { setShowInput(false); return; }
    // Also push into FIELD_CONFIG.MODEL_NAME.options so it persists across re-renders
    if (!baseOptions.includes(v)) baseOptions.push(v);
    setExtra(p => p.includes(v) ? p : [...p, v]);
    onChange({ target: { value: v } });
    setInputVal("");
    setShowInput(false);
  }

  if (showInput) {
    return (
      <div className="flex gap-2 mt-1">
        <input
          autoFocus
          type="text"
          className="flex-1 px-3 py-2 rounded-xl border text-[rgb(var(--fg))] bg-[rgb(var(--card))] border-[rgb(var(--border))] outline-none focus:border-[rgb(var(--primary))] text-sm"
          placeholder="Enter model name..."
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") handleAdd();
            if (e.key === "Escape") { setShowInput(false); setInputVal(""); }
          }}
        />
        <button
          onClick={handleAdd}
          className="px-3 py-2 rounded-xl text-sm font-semibold text-white shrink-0"
          style={{ background: "rgb(var(--primary))" }}
        >Add</button>
        <button
          onClick={() => { setShowInput(false); setInputVal(""); }}
          className="px-3 py-2 rounded-xl text-sm border border-[rgb(var(--border))] shrink-0"
        >✕</button>
      </div>
    );
  }

  return (
    <select
      className={"styled-select " + inputCls}
      value={value ?? ""}
      onChange={e => e.target.value === "__add_new__" ? setShowInput(true) : onChange(e)}
    >
      <option value="">-- Select --</option>
      {all.map(o => <option key={o} value={o}>{o}</option>)}
      <option value="__add_new__">+ Add new model</option>
    </select>
  );
}

function renderInput(k, v, onChange, allData) {
  const cfg = FIELD_CONFIG[k];
  if (cfg?.type === "date") {
    return <input type="date" className={inputCls} value={toDateValue(v)} onChange={(e) => {
      const ddmmyyyy = pickerToddmmyyyy(e.target.value);
      onChange({ target: { value: ddmmyyyy } });
    }} />;
  }
  if (cfg?.type === "phone") {
    return (
      <input type="tel" inputMode="numeric" maxLength={10} className={inputCls} value={v ?? ""}
        onChange={(e) => { if (/^\d{0,10}$/.test(e.target.value)) onChange(e); }} />
    );
  }
  if (cfg?.type === "select") {
    if (cfg.addable) {
      return <AddableSelect value={v} onChange={onChange} baseOptions={cfg.options} />;
    }
    let options = cfg.options;
    let placeholder = "-- Select --";
    if (cfg.cascade) {
      const parentVal = allData?.[cfg.cascade] || "";
      options = parentVal ? (STATE_CITIES[parentVal] || []) : [];
      placeholder = parentVal ? "-- Select City --" : "-- Select State First --";
    }
    return (
      <select className={"styled-select " + inputCls} value={v ?? ""} onChange={onChange}>
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return <input type="text" className={inputCls} value={v ?? ""} onChange={onChange} />;
}

export default function RecordDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, permissions } = useAuth();

  const [item, setItem]         = useState(null);
  const [editData, setEditData] = useState(null);
  const [msg, setMsg]           = useState("");
  const [callers, setCallers]   = useState([]);
  const [assignedTo, setAssignedTo] = useState("");
  const [callerOpen, setCallerOpen] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [newRemark, setNewRemark] = useState("");

  const role       = user?.role;
  const canEdit    = can(permissions, "records.edit",   role);
  const canDelete  = can(permissions, "records.delete", role);
  const canAssign  = can(permissions, "records.assign", role);
  const isSuperAdmin = role === "SUPER_ADMIN";

  async function load() {
    const { data } = await api.get(`/records/${id}`);
    setItem(data.item);
    setEditData(Object.fromEntries(Object.entries(data.item.data || {})));
    setAssignedTo(data.item.assignedTo?._id || "");
  }

  async function loadCallers() {
    const { data } = await api.get("/users/callers");
    setCallers(data.callers);
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { if (canAssign) loadCallers(); /* eslint-disable-next-line */ }, [canAssign]);

  async function save() {
    const missing = REQUIRED_FIELDS.filter(f => !editData?.[f]?.toString().trim());
    if (missing.length) {
      setMsg(`❌ Required: ${missing.map(f => FIELD_LABELS[f] || f).join(", ")}`);
      return;
    }
    setMsg(""); setSaving(true);
    try {
      await api.put(`/records/${id}`, { data: editData });
      setMsg("✅ Saved successfully");
      await load();
    } catch { setMsg("❌ Save failed"); }
    finally { setSaving(false); }
  }

  async function refreshCallData() {
    const batchId = editData?.dhwani_batch_id;
    if (!batchId) return;
    setRefreshing(true); setMsg("");
    try {
      const { data } = await api.post(`/records/batch/${batchId}/refresh`);
      setMsg(`✅ Call data refreshed (${data.batch_status})`);
      await load();
    } catch { setMsg("❌ Refresh failed"); }
    finally { setRefreshing(false); }
  }

  async function saveAssign() {
    await api.put(`/records/${id}/assign`, { assignedTo: assignedTo || null });
    setMsg("✅ TC NAME assigned");
    await load();
  }

  async function del() {
    if (!window.confirm("Delete this record?")) return;
    await api.delete(`/records/${id}`);
    nav("/records");
  }

  if (!item) {
    return (
      <Layout>
        <PageLoader message="Loading record..." />
      </Layout>
    );
  }

  // Sort entries
  const sortedData = Object.entries(editData || {}).sort(([a], [b]) => {
    const ai = FIELD_ORDER.indexOf(a), bi = FIELD_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1; if (bi === -1) return -1;
    return ai - bi;
  });
  const dataMap = Object.fromEntries(sortedData);

  return (
    <Layout>
      <div className="max-w-5xl flex flex-col gap-5">

        {/* ── Header bar ── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <button
              onClick={() => nav("/records")}
              className="flex items-center gap-1.5 text-xs font-semibold mb-2 opacity-50 hover:opacity-100 transition-opacity"
              style={{ color: "rgb(var(--fg))" }}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Records
            </button>
            <div className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: "rgb(var(--primary))", opacity: 0.7 }}>
              Record Detail
            </div>
            <h1 className="text-xl font-bold tracking-tight">
              {editData?.CUSTOMER_NAME || "Record"}
            </h1>
            <p className="text-xs opacity-30 mt-0.5 font-mono">{item._id}</p>
          </div>

          <div className="flex gap-2 items-center flex-wrap">
            {msg && (
              <div className={`text-sm px-3 py-1.5 rounded-xl font-medium border ${
                msg.startsWith("✅") ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-600 border-red-200"
              }`}>
                {msg}
              </div>
            )}
            {canEdit && (
              <Button onClick={save} loading={saving}>Save Changes</Button>
            )}
            {/* {canDelete && (
              <button
                onClick={del}
                className="px-4 py-2 rounded-xl text-sm font-semibold border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
              >
                Delete
              </button>
            )} */}
          </div>
        </div>

        {/* ── TC NAME assignment ── */}
        <Card className="overflow-visible">
          <div className="px-5 py-3 border-b border-[rgb(var(--border))]" style={{ background: "rgba(var(--primary),0.04)" }}>
            <div className="text-xs font-bold uppercase tracking-widest" style={{ color: "rgb(var(--primary))" }}>
              TC Name Assignment
            </div>
          </div>
          <div className="p-5">
            {canAssign ? (
              <div className="flex gap-3 items-end flex-wrap">
                <div className="flex-1 min-w-48">
                  <label className="block text-[11px] font-semibold uppercase tracking-wider opacity-50 mb-1.5">
                    Assign Caller
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setCallerOpen(p => !p)}
                      className="styled-select w-full flex items-center justify-between px-3 py-2 rounded-xl border bg-[rgb(var(--card))] border-[rgb(var(--border))] text-[rgb(var(--fg))] text-sm outline-none"
                    >
                      <span className={assignedTo ? "" : "opacity-40"}>
                        {callers.find(c => c._id === assignedTo)?.name || "-- Unassigned --"}
                      </span>
                    </button>
                    {callerOpen && (
                      <div className="absolute z-20 mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] shadow-lg overflow-hidden">
                        <div className="max-h-[180px] overflow-y-auto">
                          {[{ _id: "", name: "-- Unassigned --" }, ...callers].map(c => (
                            <div
                              key={c._id}
                              onClick={() => { setAssignedTo(c._id); setCallerOpen(false); }}
                              className="px-3 py-2 text-sm cursor-pointer hover:bg-[rgba(var(--primary),0.06)] border-b border-[rgb(var(--border))] last:border-b-0"
                              style={c._id === assignedTo ? { background: "rgba(var(--primary),0.1)", color: "rgb(var(--primary))", fontWeight: 600 } : {}}
                            >
                              {c.name}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <Button onClick={saveAssign}>Assign</Button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold"
                  style={{ background: "rgb(var(--primary))" }}
                >
                  {(item.assignedTo?.name || "U")[0].toUpperCase()}
                </div>
                <div>
                  <div className="text-sm font-semibold">{item.assignedTo?.name || "Unassigned"}</div>
                  <div className="text-xs opacity-40">Assigned Caller</div>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* ── Field groups ── */}
        {FIELD_GROUPS.map(group => {
          const groupEntries = group.fields
            .filter(f => f in dataMap)
            .map(f => [f, dataMap[f]]);

          if (groupEntries.length === 0) return null;

          return (
            <Card key={group.label} className="overflow-hidden">
              {/* Group header */}
              <div
                className="flex items-center justify-between gap-2.5 px-5 py-3 border-b border-[rgb(var(--border))]"
                style={{ background: "rgba(var(--primary),0.04)" }}
              >
                <div className="flex items-center gap-2.5">
                  <span className="text-base">{group.icon}</span>
                  <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "rgb(var(--primary))" }}>
                    {group.label}
                  </span>
                </div>
                {group.label === "Loyalty Program" && editData?.dhwani_batch_id && (
                  <button
                    onClick={refreshCallData}
                    disabled={refreshing}
                    className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-50"
                    style={{ borderColor: "rgb(var(--primary))", color: "rgb(var(--primary))" }}
                  >
                    <svg className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {refreshing ? "Refreshing..." : "Refresh Call Data"}
                  </button>
                )}
              </div>

              {/* Fields grid */}
              <div className="p-5 grid md:grid-cols-2 gap-4">
                {groupEntries.map(([k, v]) => {
                  const isFullWidth = k === "dhwani_ai_summary" || k === "dhwani_ai_issues";
                  const isLink = k === "dhwani_recording_url" && v;
                  const isDate = FIELD_CONFIG[k]?.type === "date";
                  const displayVal = isDate ? toDisplayDate(v) : String(v ?? "");
                  return (
                    <div key={k} className={isFullWidth ? "md:col-span-2" : ""}>
                      <label className="block text-[11px] font-bold uppercase tracking-wider opacity-50">
                        {FIELD_LABELS[k] || k}
                        {REQUIRED_FIELDS.includes(k) && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
                      </label>
                      {isLink ? (
                        <div className="mt-1 px-3 py-2 rounded-xl border border-[rgb(var(--border))] text-sm min-h-[38px] bg-[rgba(var(--primary),0.02)] overflow-hidden">
                          <a href={v} target="_blank" rel="noreferrer" className="underline flex items-center gap-1" style={{ color: "rgb(var(--primary))" }}>
                            <span>🎙️ Open Recording</span>
                          </a>
                        </div>
                      ) : isFullWidth ? (
                        <div className="mt-1 px-3 py-2 rounded-xl border border-[rgb(var(--border))] text-sm bg-[rgba(var(--primary),0.02)] whitespace-pre-wrap leading-relaxed min-h-[80px]">
                          {displayVal || <span className="opacity-30">—</span>}
                        </div>
                      ) : k === "TC_NAME" ? (
                        <div className="mt-1 px-3 py-2 rounded-xl border border-[rgb(var(--border))] text-sm min-h-[38px] opacity-50 cursor-not-allowed select-none" style={{ background: "rgba(128,128,128,0.08)" }}>
                          {displayVal || <span className="opacity-30">—</span>}
                        </div>
                      ) : canEdit && !k.startsWith("dhwani_") ? (
                        renderInput(k, v, (e) => setEditData(p => {
                          const next = { ...p, [k]: e.target.value };
                          if (k === "DEALER_STATE") next.DEALER_CITY = "";
                          return next;
                        }), editData)
                      ) : (
                        <div className="mt-1 px-3 py-2 rounded-xl border border-[rgb(var(--border))] text-sm min-h-[38px] bg-[rgba(var(--primary),0.02)]">
                          {displayVal || <span className="opacity-30">—</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Remarks section */}
              {group.hasRemarks && (
                <div className="px-5 pb-5">
                  <label className="block text-[11px] font-bold uppercase tracking-wider opacity-50 mb-2">Remarks</label>

                  {/* Past remarks */}
                  {(() => {
                    let remarks = editData?.REMARKS;
                    if (!remarks) {
                      // Migrate old single REMARK to array
                      const oldRemark = editData?.REMARK;
                      const oldDate = editData?.REMARK_DATE;
                      remarks = oldRemark ? [{ text: oldRemark, date: oldDate || "" }] : [];
                    }
                    return remarks.length > 0 ? (
                      <div className="space-y-2 mb-3 max-h-[250px] overflow-y-auto">
                        {[...remarks].reverse().map((r, i) => (
                          <div key={i} className="px-3 py-2 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--primary),0.02)]">
                            <p className="text-sm break-words overflow-hidden">{r.text}</p>
                            <p className="text-[11px] opacity-40 mt-1">{r.date ? toDisplayDate(r.date) : "—"}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm opacity-30 mb-3">No remarks yet.</p>
                    );
                  })()}

                  {/* Add new remark */}
                  {canEdit && (
                    <div className="flex gap-2 items-end" > 
                      <div className="flex-1 " >
                        <textarea
                          className={inputCls + " resize-none"}
                          rows={2}
                          placeholder="Add a remark...."
                          value={newRemark}
                          onChange={(e) => setNewRemark(e.target.value)}
                          style={{ border: "2px solid rgb(var(--primary))", background: "rgba(var(--primary),0.04)" }}
                        />
                      </div>
                      <button
                        onClick={() => {
                          if (!newRemark.trim()) return;
                          const now = new Date();
                          const dd = String(now.getDate()).padStart(2, "0");
                          const mm = String(now.getMonth() + 1).padStart(2, "0");
                          const yyyy = now.getFullYear();
                          const dateStr = `${dd}/${mm}/${yyyy}`;
                          const prev = editData?.REMARKS || (editData?.REMARK ? [{ text: editData.REMARK, date: editData.REMARK_DATE || "" }] : []);
                          setEditData(p => ({
                            ...p,
                            REMARKS: [...prev, { text: newRemark.trim(), date: dateStr }],
                            REMARK: newRemark.trim(),
                            REMARK_DATE: dateStr,
                          }));
                          setNewRemark("");
                        }}
                        className="px-4 py-2 rounded-xl text-sm font-semibold text-white hover:opacity-90 transition-opacity whitespace-nowrap"
                        style={{ background: "rgb(var(--primary))" }}
                      >
                        Add
                      </button>
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}

        {/* ── Bottom save bar ── */}
        {/* {canEdit && (
          <div className="flex items-center gap-3 pb-4">
            <Button onClick={save} loading={saving}>Save Changes</Button>
            {msg && (
              <div className={`text-sm px-3 py-1.5 rounded-xl font-medium border ${
                msg.startsWith("✅") ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-600 border-red-200"
              }`}>
                {msg}
              </div>
            )}
          </div>
        )} */}

      </div>
    </Layout>
  );
}
